import { isNumber } from 'lodash';
import { KeyValueCollection } from '@ipreo/muni-shared';
import { GameDayCalculatedData } from '../../gameday/data/gameDay-data.calculation';
import { GameDayModel } from '../../models/gameday-model';

type ColumnFunction = (m: GameDayModel.Maturity) => number | string | string[];

type TotalFunction = () => number | number[];

export interface RetailData {
  headers: string[];
  rows: any[];
  totals: KeyValueCollection<number>;
}

interface ColumnDefinition {
  id: string;
  fn: ColumnFunction;
  multiple?: boolean;
  total?: number | TotalFunction;
}

const combineFn = fn => (p, m) => {
  p[m.id] = fn(m);
  return p;
};

export function getRetailReportsColumns(
  data: GameDayCalculatedData
): RetailData {
  let { categories } = data.deal;
  const { maturities, series } = data.deal;
  const { orders } = data;

  categories = categories || [];

  const seriesMap = series.reduce(combineFn(s => s), {});
  const maturityAmounts = maturities.reduce(
    combineFn(m => ({
      categories: {},
      stock: 0,
      institutional: 0,
      retail: 0,
      total: 0
    })),
    {}
  );
  const categoriesAmount = {};

  // iterate though orders and collect amounts.
  // use forEach for optimization
  orders.forEach(o => {
    const maturity = maturityAmounts[o.maturityId];
    const orderTypeCode = o.orderType && o.orderType.code;
    const categoryId = o.category && o.category.name && o.category.id;
    const amount = o.amount / 1000;

    maturity.total += amount;

    if (
      o.retailInd &&
      !categoryId &&
      (orderTypeCode === 'ND' ||
        orderTypeCode === 'GN' ||
        orderTypeCode === 'MEM')
    ) {
      maturity.retail += amount;
    }
    if (!o.retailInd && (orderTypeCode === 'ND' || orderTypeCode === 'GN')) {
      maturity.institutional += amount;
    }
    if (!o.retailInd && orderTypeCode === 'MEM') {
      maturity.stock += amount;
    }

    if (!categoryId) {
      return;
    }

    if (!maturity.categories[categoryId]) {
      maturity.categories[categoryId] = 0;
    }
    maturity.categories[categoryId] += amount;

    if (!categoriesAmount[categoryId]) {
      categoriesAmount[categoryId] = 0;
    }
    categoriesAmount[categoryId] += amount;
  });

  const columnsDefinition: ColumnDefinition[] = [
    {
      id: 'maturity',
      fn: m =>
        `${seriesMap[m.seriesId].code}: ${m.maturityDate.format('MM/DD/YY')}`
    },
    {
      id: 'amount',
      fn: m => m.size / 1000,
      total: 0
    },
    {
      id: 'coupon',
      fn: m => m.coupon
    },
    {
      multiple: true,
      id: 'categories',
      fn: m => categories.map(c => maturityAmounts[m.id].categories[c.id] || 0),
      total: () => categories.map(c => categoriesAmount[c.id] || 0)
    },
    {
      id: 'stock',
      fn: m => maturityAmounts[m.id].stock,
      total: 0
    },
    {
      id: 'institutional',
      fn: m => maturityAmounts[m.id].institutional,
      total: 0
    },
    {
      id: 'retail',
      fn: m => maturityAmounts[m.id].retail,
      total: 0
    },
    {
      id: 'total',
      fn: m => maturityAmounts[m.id].total,
      total: 0
    },
    {
      id: 'balance',
      fn: m => {
        const size = m.size / 1000;
        if (maturityAmounts[m.id].total < size) {
          return size - maturityAmounts[m.id].total;
        }
        return 0;
      },
      total: 0
    }
  ];

  const rows = maturities.map((m: GameDayModel.Maturity) => {
    const row = {} as KeyValueCollection<number | string | string[]>;

    columnsDefinition.forEach(columnDefinition => {
      if (columnDefinition.multiple) {
        row[columnDefinition.id] = columnDefinition.fn(m);
      } else {
        const value = columnDefinition.fn(m);
        row[columnDefinition.id] = value;
        if (isNumber(columnDefinition.total)) {
          columnDefinition.total =
            (columnDefinition.total as number) + (value as number);
        }
      }
    });

    return row;
  });

  const totals = {} as KeyValueCollection<number>;

  columnsDefinition.forEach(columnDefinition => {
    if (isNumber(columnDefinition.total)) {
      totals[columnDefinition.id] = columnDefinition.total;
    } else if (columnDefinition.multiple) {
      totals[columnDefinition.id] = columnDefinition.total();
    }
  });

  const headers = categories.map(c => c.name);

  return {
    headers,
    rows,
    totals
  };
}

export function getSortedColumns(list, key) {
  function compare(a, b) {
    return a[key] - b[key];
  }
  return list.sort(compare).map(item => ({ ...item, position: undefined }));
}
